import craftsmanship from '../shared/craftsmanship';
const array = value => Array.isArray(value) ? value : [];

export const isItemCommission = details => Boolean(details) && (
  Boolean(details.manufacturing) ||
  String(details.serviceType || '').toLowerCase() === 'craft item' ||
  array(details.consideration).some(entry => entry.side === 'npc' && entry.kind === 'item' && entry.timing === 'on_completion')
);

const formatPayment = currency => Object.entries(currency || {})
  .filter(([, quantity]) => Number(quantity) > 0)
  .map(([name, quantity]) => `${quantity} ${name.toLowerCase()}`)
  .join(', ');

const validMinute = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;

const formatElapsed = minutes => {
  let remaining = Math.floor(minutes);
  const parts = [];
  for (const [size, label] of [[10080, 'week'], [1440, 'day'], [60, 'hour'], [1, 'minute']]) {
    const quantity = Math.floor(remaining / size);
    remaining %= size;
    if (quantity) parts.push(`${quantity} ${label}${quantity === 1 ? '' : 's'}`);
  }
  return parts.join(', ') || 'Less than a minute';
};

const formatAgreementDate = (date, minute) => {
  const time = `${String(Math.floor(minute % 1440 / 60)).padStart(2, '0')}:${String(Math.floor(minute % 60)).padStart(2, '0')}`;
  return date?.season && date?.day && date?.year
    ? `${date.season} ${date.day}, year ${date.year}${date.age != null ? `, age ${date.age}` : ''}, ${time}`
    : `Game day ${Math.floor(minute / 1440) + 1}, ${time}`;
};

export default function ContractTerms({ details, accepted = false, showAgreement = false, currentGameMinute }) {
  if (!details) return null;
  const payments = array(details.consideration).filter(entry => entry.side === 'pc' && entry.kind === 'currency');
  const lodging = Boolean(details.lodging?.roomPlaceId);
  const agreedAtMinute = validMinute(details.agreedAtMinute) ? details.agreedAtMinute : details.gameplayWork?.plan?.acceptedMinute;
  const hasAgreementTime = validMinute(agreedAtMinute);
  const elapsed = hasAgreementTime && validMinute(currentGameMinute) && currentGameMinute >= agreedAtMinute
    ? formatElapsed(currentGameMinute - agreedAtMinute) : null;
  const timingLabels = { immediate: accepted ? 'at acceptance' : 'now', on_completion: 'on completion', future: 'later', conditional: 'conditional', none: '' };
  if (!details.durationText && !details.dueText && !payments.length && !isItemCommission(details) && !details.paidText && !hasAgreementTime) return null;
  return (
    <dl className="contract-terms">
      {showAgreement ? <><dt>Agreement</dt><dd>{details.outputText || details.output}</dd></> : null}
      {accepted ? <>
        <dt>Agreement date</dt><dd>{hasAgreementTime ? formatAgreementDate(details.agreementDate, agreedAtMinute) : 'Not recorded'}</dd>
        <dt>Elapsed game time since agreement</dt><dd>
          {elapsed || 'Unavailable'}
          {elapsed && details.durationText ? <p>Compare with the estimate below; its stated starting conditions still apply.</p> : null}
        </dd>
      </> : null}
      {details.manufacturing?.qualityLevel != null ? <><dt>Agreed craftsmanship</dt><dd>{craftsmanship.formatQuality(details.manufacturing.qualityLevel)} per item</dd></> : null}
      {lodging ? <><dt>Assigned room</dt><dd>{details.lodgingRoomLabel || 'Assigned private room'}</dd>
        <dt>Room key</dt><dd>{details.lodgingKeyLabel || 'Room key'} — borrowed; ownership retained by the provider.</dd></> : null}
      {details.durationText ? <><dt>{lodging ? 'Reservation term' : 'Estimated completion'}</dt><dd>{details.durationText}</dd></> : null}
      {details.dueText ? <><dt>Timing / deadline terms</dt><dd>{details.dueText}</dd></> : null}
      {details.durationText && !lodging ? <><dt>Deadline</dt><dd>The estimate is not a promised deadline; any agreed deadline is stated in the timing terms.</dd></> : null}
      {payments.length > 0 ? <><dt>Payment</dt><dd>{payments.map((payment, index) => (
        <p key={index}>{formatPayment(payment.currency)} {timingLabels[payment.timing] || payment.timing}{payment.description ? ` — ${payment.description}` : ''}</p>
      ))}</dd></> : null}
      {accepted && details.paidText ? <><dt>Recorded payment</dt><dd>{details.paidText}</dd></> : null}
      {isItemCommission(details) ? <><dt>Delivery</dt><dd>Collection when finished; the commissioned item is delivered on completion.</dd></> : null}
    </dl>
  );
}
